import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { concat, finalize, from, Subscription, switchMap } from 'rxjs'
import * as Yup from 'yup'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { LoadingButton } from '@mui/lab'
import { FormHelperText, Grid, Link, Stack, Typography } from '@mui/material'
import {
  AwsErrorCodes,
  RecaptchaActions,
  useRollbarInstance,
  useSubscriptionRef,
} from '@procom-labs/common'
import { TextHookField, useAlert } from '@procom-labs/molecules'

import { Header } from '@auth-portal/components/header'
import { VerifyDigitalCode } from '@auth-portal/components/verify-digital-code'
import { useAuthService, useRecaptcha } from '@auth-portal/hooks'
import { FlowRequestType, IInitialAuthentication } from '@auth-portal/types'
import { yupResolver } from '@hookform/resolvers/yup'

interface FormValues {
  email: string
  password: string
  confirmPassword: string
}

export const SignupCompleteForm: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { getRecaptchaToken } = useRecaptcha()
  const { t, i18n } = useTranslation('main')
  const [searchParams] = useSearchParams()
  const { addAlert } = useAlert()
  const navigate = useNavigate()

  const authService = useAuthService()
  const rollbarInstance = useRollbarInstance()

  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOtpValid, setIsOtpValid] = useState(false)
  const [showOtpVerifyScreen, setShowOtpVerifyScreen] = useState(false)

  const emailParam = decodeURIComponent(searchParams.get('email') ?? '')
  const idParam = searchParams.get('id')
  const token = searchParams.get('token')
  const otp = searchParams.get('otp')
  const requestType = searchParams.get('requestType')

  const resendSubscriptionRef = useSubscriptionRef()
  const otpSubscriptionRef = useSubscriptionRef()
  const resetOtpSubscriptionRef = useSubscriptionRef()
  const submitSubscriptionRef = useSubscriptionRef()

  const commonPasswordSchema = useMemo(
    () =>
      Yup.string()
        .required('form.login.password.requiredError')
        .min(6, t('form.passwordValidation.min6Characters'))
        .matches(/\d+/, t('form.passwordValidation.min1Number'))
        .matches(/[A-Z]+/, t('form.passwordValidation.min1CapitalLetter'))
        .matches(/[a-z]+/, t('form.passwordValidation.min1LowerCaseLetter')),
    [t]
  )

  const passwordValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        password: commonPasswordSchema,
        confirmPassword: commonPasswordSchema.oneOf(
          [Yup.ref('password')],
          t('form.passwordValidation.passwordNotMatch')
        ),
      }),
    [t, commonPasswordSchema]
  )

  const formInstance = useForm<FormValues>({
    defaultValues: {
      email: emailParam,
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
    resolver: yupResolver(passwordValidationSchema),
  })

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formInstance

  const handleGoBack = useCallback(() => {
    navigate('/', {
      replace: true,
    })
  }, [navigate])

  const onSubmit = useCallback(
    (data: FormValues) => {
      setIsLoading(true)
      if (emailParam && idParam) {
        setIsLoading(true)
        const email = emailParam
        const code = idParam
        submitSubscriptionRef.current = authService
          .initialLogin(data.password)
          .pipe(
            switchMap(() =>
              concat(
                authService.setUserRegisteredOn(),
                authService.verifyEmail({
                  email,
                  onboardingCode: code,
                })
              )
            ),
            finalize(() => setIsLoading(false))
          )
          .subscribe({
            next: () => {
              authService.redirectToClientPortal()
            },
            error: () => {
              addAlert({
                severity: 'error',
                message: t('somethingWrong'),
              })
            },
          })
      }
    },
    [emailParam, idParam, submitSubscriptionRef, addAlert, t]
  )

  const handleResendLink = useCallback(
    (email: string, tempPassword: string) => {
      resendSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.ResendInvitation)
      )
        .pipe(
          switchMap((recaptcha) => {
            return authService.resendUserInvitation(
              email,
              recaptcha,
              null,
              null,
              tempPassword
            )
          })
        )
        .subscribe({
          next: () => {
            addAlert({
              message: t('common.cognitoErrors.inviteExpired'),
              severity: 'error',
            })
          },
          error: () => {
            addAlert({
              message: t('somethingWrong'),
              severity: 'error',
            })
            handleGoBack()
          },
        })
    },
    [resendSubscriptionRef, getRecaptchaToken, addAlert, t, handleGoBack]
  )

  const handleAuthentication = useCallback(
    (initialAuthentication: IInitialAuthentication): Subscription => {
      return authService
        .authenticate({
          username: initialAuthentication.email,
          password: initialAuthentication.oneTimeCode,
          context: 'complete-registration',
        })
        .subscribe({
          error: (err) => {
            rollbarInstance.error(err)
            if (err.code === AwsErrorCodes.NotAuthorizedException) {
              handleResendLink(
                initialAuthentication.email,
                initialAuthentication.oneTimeCode
              )
            } else {
              handleGoBack()
            }
          },
        })
    },
    [handleGoBack, handleResendLink]
  )

  const handleOtpSignIn = useCallback(() => {
    if (isOtpValid) {
      otpSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.CodeSignIn)
      )
        .pipe(
          switchMap((recaptcha) =>
            authService.handleOtpSignIn(otp ?? '', emailParam, recaptcha)
          )
        )
        .subscribe({
          next: () => {
            authService.redirectToClientPortal({
              isOtpLogin: true,
            })
          },
          error: () => {
            addAlert({
              message: t('somethingWrong'),
              severity: 'error',
            })
          },
        })
    } else {
      // Generate Otp and redirect to verify otp screen
      resetOtpSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.RequestResetPassword)
      )
        .pipe(
          switchMap((recaptcha) =>
            authService.generateSignInOtp(emailParam, recaptcha, i18n.language)
          )
        )
        .subscribe({
          next: () => {
            setShowOtpVerifyScreen(true)
          },
          error: () => {
            addAlert({
              message: t('somethingWrong'),
              severity: 'error',
            })
          },
        })
    }
  }, [
    isOtpValid,
    otpSubscriptionRef,
    getRecaptchaToken,
    otp,
    emailParam,
    addAlert,
    t,
    resetOtpSubscriptionRef,
    i18n.language,
  ])

  // UseEffect - Wait for recaptcha to be ready before using
  useEffect(() => {
    if (executeRecaptcha) {
      setIsRecaptchaReady(true)
    }
  }, [executeRecaptcha])

  // UseEffect - Validate cognito token
  useEffect(() => {
    const subscription = new Subscription()
    if (emailParam && token && isRecaptchaReady) {
      const initialAuthentication: IInitialAuthentication = {
        email: emailParam,
        oneTimeCode: decodeURIComponent(token),
      }
      subscription.add(handleAuthentication(initialAuthentication))
    }

    return () => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [emailParam, token, isRecaptchaReady])

  // UseEffect - Validate Otp code
  useEffect(() => {
    const subscription = new Subscription()
    if (emailParam) {
      setIsLoading(true)
      subscription.add(
        from(getRecaptchaToken(RecaptchaActions.RequestResetPassword))
          .pipe(
            switchMap((recaptcha) =>
              authService.validateOtpSignIn(emailParam, otp ?? '', recaptcha)
            ),
            finalize(() => setIsLoading(false))
          )
          .subscribe({
            next: (response) => {
              setIsOtpValid(
                response.isCodeValid &&
                  !response.hasCodeExpired &&
                  !response.hasReachedMaxAttempts
              )
            },
            error: () => {
              // Silent process
              // TODO: case specific handle message
            },
          })
      )
    }

    return () => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [emailParam, otp, getRecaptchaToken])

  // UseEffect - Trigger otp sign in if otp is valid and request type is loginRedirect
  useEffect(() => {
    if (isOtpValid && requestType === FlowRequestType.loginRedirect) {
      handleOtpSignIn()
    }
  }, [isOtpValid, requestType, handleOtpSignIn])

  return (
    <Grid>
      <Header heading={t('form.completeRegistration.heading')} />

      {!showOtpVerifyScreen ? (
        <FormProvider {...formInstance}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              direction="column"
              rowGap={2}
              sx={{
                px: {
                  xs: 1,
                  sm: 4,
                  lg: 5,
                  xl: 7,
                  k2: 12.5,
                },
              }}
            >
              <TextHookField
                control={control}
                name="email"
                label={t('form.email.label')}
                customLabel={false}
                disabled
              />

              <TextHookField
                control={control}
                name="password"
                type="password"
                label={t('form.changePassword.label')}
                customLabel={false}
              />

              <FormHelperText sx={{ display: 'flex', ml: 0, my: 1 }}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 17, mr: 1, mt: 0.1 }}
                  color="success"
                />
                {t('form.passwordValidation.passwordValidation')}
              </FormHelperText>

              <TextHookField
                control={control}
                name="confirmPassword"
                type="password"
                label={t('form.changePassword.label3')}
                customLabel={false}
              />

              <LoadingButton
                variant="contained"
                size="large"
                color="secondary"
                type="submit"
                fullWidth
                disabled={!emailParam}
                loading={isSubmitting || isLoading}
              >
                {t('common.btn.submit')}
              </LoadingButton>

              <Typography
                component="p"
                variant="subtitle1"
                my={2}
                sx={{ textAlign: 'center' }}
              >
                {t('form.contractorSignup.or')}
              </Typography>

              <LoadingButton
                fullWidth
                size="large"
                variant="outlined"
                onClick={handleOtpSignIn}
              >
                {t('form.createAccount.oneTimeCode')}
              </LoadingButton>

              <Link
                component={RouterLink}
                underline="none"
                to="/"
                sx={{ mt: 2.5, mb: 5 }}
              >
                <Typography
                  variant="body1Bold700"
                  lineHeight={1.5}
                  component="span"
                >
                  {t('form.reset.backLoginBtn')}
                </Typography>
              </Link>
            </Stack>
          </form>
        </FormProvider>
      ) : (
        <VerifyDigitalCode email={emailParam} />
      )}
    </Grid>
  )
}
